const text = (value: string | undefined): string | undefined => value || undefined

export const env = {
  get nodeEnv() {
    return process.env.NODE_ENV
  },
  get nextRuntime() {
    return text(process.env.NEXT_RUNTIME)
  },
  get vercelEnv() {
    return text(process.env.VERCEL_ENV)
  },
  get publicVercelEnv() {
    return text(process.env.NEXT_PUBLIC_VERCEL_ENV)
  },
  get appVersion() {
    return text(process.env.NEXT_PUBLIC_APP_VERSION)
  },
  get baseUrl() {
    return text(process.env.NEXT_PUBLIC_BASE_URL)
  },
  get apiBaseUrl() {
    return text(process.env.NEXT_PUBLIC_NELO_API_BASE_URL)
  },
  get sandboxApiBaseUrl() {
    return text(process.env.NEXT_PUBLIC_NELO_API_BASE_URL_SANDBOX)
  },
  get signupOrigin() {
    return text(process.env.NEXT_PUBLIC_SIGNUP_ORIGIN)
  },
  get defaultRegion() {
    return text(process.env.NEXT_PUBLIC_DEFAULT_REGION)
  },
  get browserSessionOrigins() {
    return text(process.env.NEXT_PUBLIC_BROWSER_SESSION_ORIGINS)
  },
  get stripeKey() {
    return text(process.env.NEXT_PUBLIC_STRIPE_KEY)
  },
  get datadogApplicationId() {
    return text(process.env.NEXT_PUBLIC_DATADOG_RUM_APPLICATION_ID)
  },
  get datadogClientToken() {
    return text(process.env.NEXT_PUBLIC_DATADOG_RUM_CLIENT_TOKEN)
  },
  get datadogSessionSampleRate() {
    return text(process.env.NEXT_PUBLIC_DATADOG_RUM_SESSION_SAMPLE_RATE)
  },
  get logLevel() {
    return text(process.env.LOG_LEVEL)
  },
  get revalidateSecret() {
    return text(process.env.REVALIDATE_SECRET)
  },
  get showcaseOpen() {
    return process.env.SHOWCASE_OPEN === "1"
  },
}

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value)
    return protocol === "https:" || protocol === "http:"
  } catch {
    return false
  }
}

const isPercentage = (value: string): boolean => {
  const rate = Number(value)
  return Number.isFinite(rate) && rate >= 0 && rate <= 100
}

const checks: ReadonlyArray<{ name: string; value: () => string | undefined; valid: (value: string) => boolean; expected: string }> = [
  { name: "NEXT_PUBLIC_BASE_URL", value: () => env.baseUrl, valid: isHttpUrl, expected: "an http(s) URL" },
  { name: "NEXT_PUBLIC_NELO_API_BASE_URL", value: () => env.apiBaseUrl, valid: isHttpUrl, expected: "an http(s) URL" },
  { name: "NEXT_PUBLIC_NELO_API_BASE_URL_SANDBOX", value: () => env.sandboxApiBaseUrl, valid: isHttpUrl, expected: "an http(s) URL" },
  { name: "NEXT_PUBLIC_SIGNUP_ORIGIN", value: () => env.signupOrigin, valid: isHttpUrl, expected: "an http(s) URL" },
  { name: "NEXT_PUBLIC_DEFAULT_REGION", value: () => env.defaultRegion, valid: (value) => /^[a-z]{2}$/.test(value), expected: "a lowercase two-letter country code" },
  { name: "NEXT_PUBLIC_DATADOG_RUM_SESSION_SAMPLE_RATE", value: () => env.datadogSessionSampleRate, valid: isPercentage, expected: "a number from 0 to 100" },
]

export const envProblems = (): string[] =>
  checks.flatMap(({ name, value, valid, expected }) => {
    const current = value()
    return current === undefined || valid(current) ? [] : [`${name} must be ${expected}, got "${current}"`]
  })

export const assertValidEnv = (): void => {
  const problems = envProblems()
  if (problems.length > 0) throw new Error(`Invalid environment:\n${problems.join("\n")}`)
}

const isE2E = process.env.NEXT_PUBLIC_E2E === "true"

export const testOverridesAllowed = env.nodeEnv !== "production" || isE2E

export const devShowcaseEnabled = env.nodeEnv !== "production" || env.publicVercelEnv === "preview"
